import { DataEditContextPasswordField } from 'Components/reusable/DataContext/fields/DataEditContextPasswordField'
import { DataEditContextValidation } from 'Components/reusable/DataContext/Validation'
import { getResetPasswordInitialData } from 'Components/reusable/DataContext/InitialData'
import { resetPasswordSchema } from 'Components/reusable/DataContext/ValidationSchema'
import { authAPI } from 'api/AuthAPI'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { IResetPassword } from 'types/accountInterfaces'

import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import DataContext, { NewOrExisting } from 'Components/reusable/DataContext'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'
import { useLogin, useUser } from 'hooks'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router'
import { IError } from 'types/error'
import CaleoPrimaryButton from 'Components/reusable/Buttons/CaleoPrimaryButton'
import LoginPageCard from 'Components/reusable/CaleoCustomComponents/LoginPageCard'
import LoginPageCardWrapper from 'Components/reusable/CaleoCustomComponents/LoginPageCardWrapper'
import { useTranslation } from 'react-i18next'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'

type T = IResetPassword

/**
 * Renders the ResetPassword component.
 *
 * @return {React.ReactElement} The rendered ResetPasswordCard component.
 * @notExported
 */
const ResetPassword = () => {
  const { t } = useTranslation()
  const { setNotificationPopup } = useNotificationPopup()
  const [submitError, setSubmitError] = useState<IError>()
  const navigate = useNavigate()

  const [data, setData] = useState<NewOrExisting<T>>(getResetPasswordInitialData())
  const schema = resetPasswordSchema()
  const localeBase = 'resetPassword'
  const [submitLocally, setSubmitLocally] = useState<boolean>(false)
  const { loggingIn, loggedIn, loginLoading } = useLogin()
  const location = useLocation()
  const { groupsReady, groups } = useUser()
  const { token } = useParams()
  const [passwordError, setPasswordError] = useState<string>()

  const buttonsEnabled = !loggingIn

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (submitLocally) {
        const submitData = {
          password: data.password,
          token: token,
        }
        try {
          await authAPI.resetPassword(submitData, controller)
          setNotificationPopup({
            message: t('password.activated'),
            type: 'success',
            duration: 'long',
          })
          navigate('/login')
        } catch (err) {
          const error = err as IError
          if (error.response?.status === 400 && error.response?.data === 'same-password-error') {
            setPasswordError(error.response?.data)
          } else {
            setSubmitError(error)
            setSubmitLocally(false)
          }
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [data.password, navigate, setNotificationPopup, submitLocally, t, token])

  useEffect(() => {
    if (loggedIn && groupsReady) {
      if (location.state?.url) {
        navigate(location.state?.url)
      } else if (groups && groups.includes('sales')) {
        navigate('/employees')
      } else {
        navigate('/profile')
      }
    }
  }, [loggedIn, groupsReady, location.state?.url, groups, navigate])

  useEffect(() => {
    if (passwordError) {
      setPasswordError(undefined)
      setSubmitLocally(false)
    }
  }, [data.password, data.repeatPassword, passwordError])

  if (!data) {
    return <LoadingIndicator />
  }

  if (submitError) {
    return <ErrorOverlay error={submitError} setOpen={setSubmitError} />
  }

  return (
    <LoginPageCardWrapper>
      {loginLoading ? (
        <LoadingIndicator />
      ) : (
        <DataContext
          data={data}
          onChange={setData}
          onSubmit={() => setSubmitLocally(true)}
          schema={schema}
          localeBase={localeBase}
        >
          <LoginPageCard header={t('resetPassword.header')}>
            <Grid container>
              <Grid mt={2} mb={passwordError ? 0 : 2} size={12}>
                <DataEditContextPasswordField<IResetPassword>
                  field="password"
                  autoComplete="new-password"
                  name="new-password"
                  required
                  fullWidth
                />
                <DataEditContextPasswordField<IResetPassword>
                  field="repeatPassword"
                  autoComplete="new-password2"
                  name="new-password2"
                  required
                  fullWidth
                />
              </Grid>
              {passwordError && (
                <Grid mb={2} size={12}>
                  <CardContentText type="error">{t(passwordError)}</CardContentText>
                </Grid>
              )}
            </Grid>
            <Grid container justifyContent="center" spacing={1} alignItems="center">
              <Grid container alignItems="center" spacing={1} size={12}>
                <Grid size="auto">
                  <DataEditContextValidation>
                    {({ valid }) => (
                      <CaleoPrimaryButton
                        testId="button-reset-password-submit"
                        valid={(valid && !passwordError) || !buttonsEnabled}
                        clickAction={() => setSubmitLocally(true)}
                        label={t('password.reset')}
                        loading={false}
                      />
                    )}
                  </DataEditContextValidation>
                </Grid>
              </Grid>
            </Grid>
          </LoginPageCard>
        </DataContext>
      )}
    </LoginPageCardWrapper>
  )
}

export default ResetPassword
